import React, { useEffect, useState } from "react";
import styles from "./css/MyAccount.module.css";
import {
  FormControl,
  MenuItem,
  Select,
  TextField,
  Button,
} from "@mui/material";
import { countries } from "./countries";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import {
  setAddress,
  setCity,
  setCountry,
  setZipCode,
  setAppartment,
} from "../../redux/slices/userSlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Addresses = () => {
  const dispatch = useDispatch();
  const language = useSelector((state: any) => state.language.value);
  const userState = useSelector((state: any) => state.user);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const notify = (message: string, isError: boolean = false) => {
    toast(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
      type: isError ? "error" : "success",
    });
  };

  // Modified useEffect to properly handle the profile data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/auth/profile`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }
        );

        console.log("Profile response:", response.data); // Debug log

        if (response.data) {
          dispatch(setCountry(response.data.address.selectedCountry));
          dispatch(setAddress(response.data.address.address));
          dispatch(setAppartment(response.data.address.appartment));
          dispatch(setCity(response.data.address.city));
          dispatch(setZipCode(response.data.address.zipCode));
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    switch (name) {
      case "selectedCountry":
        dispatch(setCountry(value));
        break;
      case "address":
        dispatch(setAddress(value));
        break;
      case "appartment":
        dispatch(setAppartment(value));
        break;
      case "city":
        dispatch(setCity(value));
        break;
      case "zipCode":
        dispatch(setZipCode(value));
        break;
    }
  };

  useEffect(() => {
    console.log("Address state updated:", userState);
  }, [userState]);

  const updateAddress = async () => {
    setError("");
    setSuccess("");

    const addressData = {
      selectedCountry: userState.country,
      address: userState.address,
      appartment: userState.apartment,
      city: userState.city,
      zipCode: userState.zip,
    };

    try {
      await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/auth/update-address`,
        addressData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      // After successful update, refresh the data
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/auth/profile`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      if (response.data) {
        // Update Redux state with fresh data
        dispatch(setCountry(response.data.address.selectedCountry));
        dispatch(setAddress(response.data.address.address));
        dispatch(setAppartment(response.data.address.appartment));
        dispatch(setCity(response.data.address.city));
        dispatch(setZipCode(response.data.address.zipCode));
      }

      const successMsg =
        language === "RO"
          ? "Adresa a fost actualizată cu succes!"
          : "Address updated successfully!";

      setSuccess(successMsg);
      notify(successMsg);
    } catch (error) {
      console.error("Error updating address:", error);
      const errorMsg =
        language === "RO"
          ? "Eroare la actualizarea adresei. Vă rugăm să încercați din nou."
          : "Failed to update address. Please try again.";

      setError(errorMsg);
      notify(errorMsg, true);
    }
  };

  return (
    <div className={styles.productContainer}>
      <span className={styles.productTitle}>
        {language === "RO" ? "Adrese" : "Addresses"}
      </span>
      <div className={styles.productInfo}>
        <div className={styles.frame}>
          <span className={styles.productTitle7}>
            {language === "RO" ? "Schimbă adresa" : "Change address"}
          </span>
          <div className={styles.form}>
            <div className={styles.inputs}>
              <div className={styles.inputField}>
                <FormControl sx={{ width: "100%" }}>
                  <Select
                    name="selectedCountry"
                    value={userState.country}
                    onChange={handleChange}
                    displayEmpty
                    sx={{
                      color: "#517059",
                      ".MuiSelect-select": {
                        color: "#517059",
                      },
                      "& .MuiSelect-icon": {
                        color: "#517059",
                      },
                      "& .MuiSelect-outlined": {
                        color: "#517059",
                      },
                      "& .MuiInputBase-input": {
                        color: "#517059",
                      },
                      "& .MuiSelect-select:not([multiple]) option": {
                        color: "#517059",
                      },
                      "& .MuiSelect-select em": {
                        color: "#517059",
                        fontStyle: "inherit",
                      },
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <em
                            style={{
                              color: "#517059",
                              fontStyle: "inherit",
                            }}
                          >
                            {language === "RO" ? "Țară" : "Country"}
                          </em>
                        );
                      }
                      return selected;
                    }}
                  >
                    {countries.map((country) => (
                      <MenuItem
                        key={country}
                        value={country}
                        sx={{
                          color: "#517059",
                          "&.Mui-selected": {
                            color: "#517059",
                            backgroundColor: "rgba(81, 112, 89, 0.08)",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(81, 112, 89, 0.04)",
                          },
                        }}
                      >
                        {country}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className={styles.inputField}>
                <TextField
                  name="address"
                  value={userState.address}
                  onChange={handleChange}
                  placeholder={language === "RO" ? "Stradă" : "Street"}
                  variant="filled"
                  fullWidth
                  sx={{
                    input: { color: "#517059" },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#517059",
                      opacity: 1,
                    },
                    "& .MuiFilledInput-root": {
                      color: "#517059",
                      "&::placeholder": {
                        color: "#517059",
                        opacity: 1,
                      },
                    },
                  }}
                />
              </div>
              <div className={styles.inputField}>
                <TextField
                  name="appartment"
                  value={userState.apartment}
                  onChange={handleChange}
                  placeholder={language === "RO" ? "Apartament" : "Apartment"}
                  variant="filled"
                  fullWidth
                  sx={{
                    input: { color: "#517059" },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#517059",
                      opacity: 1,
                    },
                    "& .MuiFilledInput-root": {
                      color: "#517059",
                      "&::placeholder": {
                        color: "#517059",
                        opacity: 1,
                      },
                    },
                  }}
                />
              </div>
              <div className={styles.inputField}>
                <TextField
                  name="city"
                  value={userState.city}
                  onChange={handleChange}
                  placeholder={language === "RO" ? "Oraș" : "City"}
                  variant="filled"
                  fullWidth
                  sx={{
                    input: { color: "#517059" },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#517059",
                      opacity: 1,
                    },
                    "& .MuiFilledInput-root": {
                      color: "#517059",
                      "&::placeholder": {
                        color: "#517059",
                        opacity: 1,
                      },
                    },
                  }}
                />
              </div>
              <div className={styles.inputField}>
                <TextField
                  name="zipCode"
                  value={userState.zip}
                  onChange={handleChange}
                  placeholder={
                    language === "RO" ? "Cod poștal" : "ZIP / Postal Code"
                  }
                  variant="filled"
                  fullWidth
                  sx={{
                    input: { color: "#517059" },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#517059",
                      opacity: 1,
                    },
                    "& .MuiFilledInput-root": {
                      color: "#517059",
                      "&::placeholder": {
                        color: "#517059",
                        opacity: 1,
                      },
                    },
                  }}
                />
              </div>
            </div>

            {error && (
              <div
                style={{ color: "red", marginTop: "10px", fontSize: "14px" }}
              >
                {error}
              </div>
            )}
            {success && (
              <div
                style={{ color: "green", marginTop: "10px", fontSize: "14px" }}
              >
                {success}
              </div>
            )}

            <Button
              style={{
                background: "#517059",
                borderRadius: "32px",
                fontFamily: "Lora",
                marginTop: "20px",
              }}
              variant="contained"
              className={styles.buttonE}
              color="primary"
              onClick={updateAddress}
            >
              {language === "RO" ? "Actualizează" : "Update"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Addresses;
